import { PlayArrow, RotateRight, Texture } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Drawer, Grid, IconButton, InputAdornment, Slider, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ETextureOrigin, IProductTexture, ISelectedModel2D, ITexture } from '../../../@types/ContextProviderRelated'; // Added ETextureOrigin, IProductTexture
import { EActiveDrawer, EUnitType } from '../../../@types/Enums';
import AttributeModel from '../../../models/standard/AttributeModel'; // Added AttributeModel
import Model2DModel from '../../../models/standard/Model2DModel';
import { IObject } from '../../../models/standard/ObjectModel';
import { useBuilding } from '../../../provider/BuildingProvider/BuildingProvider';
import { useSelection } from '../../../provider/SelectionProvider';
import { deleteButton, immersightBlue, immersightBlue75 } from '../../../utils/colors';
import { handleWheelInput, WheelInputConfig } from '../../../utils/handleMouseEvents';
import AttributeListFixed from '../AttributeListFixed'; // Added AttributeListFixed

interface Model2DDrawerProps {
  id: string;
  open: boolean;
  onClose: () => void;
  selectedModel2D: ISelectedModel2D;
  resetSelection: () => void;
}

const Model2DDrawer: React.FC<Model2DDrawerProps> = ({ id, open, onClose, selectedModel2D, resetSelection }) => {
  const { allModels2D, setModel2DRotation, deleteModel2D, setSelectedModel: setSelectedObject, updateModel2D } = useBuilding();
  const { setActiveDrawer } = useSelection();
  const intl = useIntl();
  const { locale } = intl;

  const model2D = allModels2D[selectedModel2D.selectedModel2DIndex];

  const initialRotation = model2D ? Math.round(model2D.rotation * (180 / Math.PI)) : 0;
  const initialTextureRotation = Math.round((model2D?.texture?.rotation ?? 0) * (180 / Math.PI));

  const [rotation, setRotation] = useState<number>(initialRotation);
  const [localRotation, setLocalRotation] = useState<number>(initialRotation);
  const [shifting, setShifting] = useState<number>(model2D?.texture?.offset ?? 0);
  const [textureRotation, setTextureRotation] = useState<number>(initialTextureRotation);

  const hasModel2DTexture = Boolean(
    model2D?.texture && ((typeof model2D.texture.color === 'string' && model2D.texture.color.trim() !== '') || (model2D.texture.id !== null && model2D.texture.id !== -1)),
  );

  const getLocalizedObjectName = useCallback(
    (obj: IObject | any): string => {
      const currentLocale = locale.split('-')[0];
      if (obj.localization) {
        return obj.localization[currentLocale] || obj.localization.en || obj.name;
      }
      return obj.name;
    },
    [locale],
  );

  // --- Helper to prepare Product Texture Attributes ---
  const getProductTextureAttributes = useCallback((texture: ITexture): AttributeModel[] => {
    if (texture.textureOrigin !== ETextureOrigin.PRODUCT) return [];
    const prodTexture = texture as IProductTexture;

    // 1. Gather Object (Category) Attributes
    const objectAttrs = prodTexture.objects?.flatMap((obj) => obj.attributes || []) || [];
    const objectAttrIds = new Set(objectAttrs.map((a) => a.id));

    // 2. Filter Texture Attributes (Product Level) that are NOT in Objects
    const productAttrs = (prodTexture.attributes || []).filter((a) => !objectAttrIds.has(a.id));

    // 3. Combine
    return [...productAttrs, ...objectAttrs];
  }, []);

  useEffect(() => {
    if (model2D) {
      const newRotation = Math.round(model2D.rotation * (180 / Math.PI));
      if (newRotation !== rotation) {
        setRotation(newRotation);
        setLocalRotation(newRotation);
      }
    } else {
      onClose();
    }
  }, [model2D?.rotation, onClose, rotation]);

  useEffect(() => {
    setTextureRotation(Math.round((model2D?.texture?.rotation ?? 0) * (180 / Math.PI)));
  }, [model2D?.texture]);

  const updateTexture = (property: keyof ITexture, value: number) => {
    if (!model2D || !model2D.texture) return;
    const updatedTexture = {
      ...model2D.texture,
      [property]: property === 'rotation' ? (value * Math.PI) / 180 : value,
    };
    const updatedModel2D = new Model2DModel(
      model2D.id,
      model2D.name,
      model2D.filePath,
      model2D.fileFormat,
      model2D.fileSize,
      model2D.dimensions.length,
      model2D.dimensions.depth,
      model2D.object,
      model2D.position,
      model2D.rotation,
      updatedTexture,
      model2D.attributes,
      model2D.uuid,
    );
    updateModel2D(updatedModel2D);
  };

  const validateAndSetRotation = useCallback(
    (value: number): void => {
      if (model2D) {
        const normalizedValue = Math.round(((value % 360) + 360) % 360);
        setRotation(normalizedValue);
        setLocalRotation(normalizedValue);
        setModel2DRotation(model2D.uuid, (normalizedValue * Math.PI) / 180);
      }
    },
    [model2D, setModel2DRotation],
  );

  const handleRotationInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(event.target.value);
    if (!isNaN(newValue)) {
      setLocalRotation(newValue);
    }
  };

  const handleRotationInputBlur = () => {
    validateAndSetRotation(localRotation);
  };

  const handleRotationKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      validateAndSetRotation(localRotation);
      (event.target as HTMLInputElement).blur();
    }
  };

  const handleCloseDrawer = useCallback(() => {
    resetSelection();
    onClose();
  }, [resetSelection, onClose]);

  const handleDeleteModel2D = useCallback(() => {
    if (model2D) {
      deleteModel2D(model2D.uuid);
      resetSelection();
      onClose();
    }
  }, [model2D, deleteModel2D, resetSelection, onClose]);

  if (!model2D) {
    return null;
  }

  const rotationWheelConfig: WheelInputConfig = {
    unitType: EUnitType.METRIC,
    minMetric: 0,
    maxMetric: 360,
    minInches: 0,
    maxInches: 360,
  };

  return (
    <Drawer
      key={id}
      anchor="right"
      open={open}
      variant={open ? 'permanent' : 'temporary'}
      sx={{
        '& .MuiPaper-root': {
          top: '64px',
          zIndex: 1000,
          width: '420px',
          height: 'calc(100% - 64px)',
        },
      }}
    >
      <Grid container direction="column" p={0}>
        <Grid
          item
          sx={{
            backgroundColor: immersightBlue,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 0.5,
          }}
        >
          <Box sx={{ marginLeft: '16px' }}>
            <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
              <FormattedMessage id="Model2DDrawer.header" />
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDrawer} sx={{ color: '#fff', '&:hover': { backgroundColor: 'transparent' } }}>
            <PlayArrow sx={{ fontSize: 30 }} />
          </IconButton>
        </Grid>
        <Grid container item direction="column" spacing={3} p={3}>
          <Grid item>
            <Typography variant="h6" gutterBottom style={{ color: '#4a4a4a', letterSpacing: '0.5px' }}>
              {model2D.name}
            </Typography>
          </Grid>

          {/* LOCALIZED OBJECT NAME */}
          {model2D.object && (
            <Grid item>
              <Typography variant="subtitle2" gutterBottom style={{ color: '#4a4a4a', letterSpacing: '0.5px' }}>
                <FormattedMessage id="ElementDrawer.object" defaultMessage="Object type" />: {getLocalizedObjectName(model2D.object)}
              </Typography>
            </Grid>
          )}

          <Grid item>
            <Typography variant="subtitle2" gutterBottom style={{ color: '#4a4a4a', letterSpacing: '0.5px' }}>
              <FormattedMessage id="Model2DDrawer.rotation" />
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={localRotation}
              onChange={handleRotationInputChange}
              onBlur={handleRotationInputBlur}
              onKeyDown={handleRotationKeyDown}
              onWheel={(e) =>
                handleWheelInput(
                  e,
                  localRotation.toString(),
                  (v: string) => {
                    const newVal = parseFloat(v);
                    if (!isNaN(newVal)) {
                      setLocalRotation(newVal);
                    }
                  },
                  rotationWheelConfig,
                )
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <RotateRight />
                  </InputAdornment>
                ),
                inputProps: { min: 0, max: 360, step: 1 },
              }}
            />
          </Grid>
          <Grid item>
            <Typography variant="subtitle2" sx={{ color: immersightBlue, marginBlock: '5px' }}>
              {intl.formatMessage({ id: 'Model2DDrawer.surface', defaultMessage: 'OBERFLÄCHE' })}
            </Typography>
          </Grid>
          {!hasModel2DTexture && (
            <Grid item sx={{ marginBottom: '20px' }}>
              <Button
                variant="contained"
                startIcon={<Texture />}
                onClick={() => {
                  setSelectedObject(model2D);
                  setActiveDrawer(EActiveDrawer.TEXTURES);
                }}
                sx={{ backgroundColor: immersightBlue, color: '#fff', textTransform: 'uppercase' }}
              >
                {intl.formatMessage({
                  id: 'Model2DDrawer.changeSurface',
                  defaultMessage: 'OBERFLÄCHE ÄNDERN',
                })}
              </Button>
            </Grid>
          )}
          {model2D.texture && (model2D.texture.material || model2D.texture.color) && (
            <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: model2D.texture.color || 'transparent',
                  backgroundImage: model2D.texture.material ? `url(${model2D.texture.material.image.src})` : 'none',
                  backgroundSize: 'cover',
                  border: model2D.texture.color ? '1px solid #ccc' : 'none',
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  maxWidth: 120,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
              >
                {model2D.texture.name.length > 20 ? `${model2D.texture.name.slice(0, 20)}...` : model2D.texture.name}
              </Typography>
              <IconButton
                sx={{ color: 'red' }}
                onClick={() => {
                  const updatedModel2D = new Model2DModel(
                    model2D.id,
                    model2D.name,
                    model2D.filePath,
                    model2D.fileFormat,
                    model2D.fileSize,
                    model2D.dimensions.length,
                    model2D.dimensions.depth,
                    model2D.object,
                    model2D.position,
                    model2D.rotation,
                    null,
                    model2D.attributes,
                    model2D.uuid,
                  );
                  updateModel2D(updatedModel2D);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          )}
          {/* --- ADDED: CATEGORIES (OBJECTS) SECTION --- */}
          {model2D.texture && 'objects' in model2D.texture && model2D.texture.objects && model2D.texture.objects.length > 0 && (
            <Grid item>
              <Typography
                variant="subtitle2"
                gutterBottom
                style={{
                  color: '#4a4a4a',
                  letterSpacing: '0.5px',
                }}
              >
                {intl.formatMessage({ id: 'RoomComponentsSearchPanel.categories', defaultMessage: 'Categories' })}
                {': '}
                {model2D.texture.objects.map((obj) => getLocalizedObjectName(obj)).join(', ')}
              </Typography>
            </Grid>
          )}

          {model2D.texture?.material && (
            <>
              <Grid item>
                <Typography variant="subtitle2">{intl.formatMessage({ id: 'Model2DDrawer.textureShifting', defaultMessage: 'Verschieben' })}</Typography>
                <Slider
                  value={shifting}
                  min={-2}
                  max={2}
                  step={0.1}
                  onChange={(_, value) => {
                    const newValue = value as number;
                    setShifting(newValue);
                    updateTexture('offset', newValue);
                  }}
                  valueLabelDisplay="auto"
                  sx={{
                    color: immersightBlue,
                    '& .MuiSlider-thumb': { backgroundColor: immersightBlue75 },
                  }}
                />
              </Grid>
              {/* Texture Rotation Slider */}
              <Grid item>
                <Typography variant="subtitle2">{intl.formatMessage({ id: 'Model2DDrawer.textureRotation', defaultMessage: 'Rotieren' })}</Typography>
                <Slider
                  value={textureRotation}
                  min={0}
                  max={360}
                  step={1}
                  onChange={(_, value) => {
                    const newValue = value as number;
                    setTextureRotation(newValue);
                    updateTexture('rotation', newValue);
                  }}
                  valueLabelDisplay="auto"
                  sx={{
                    color: immersightBlue,
                    '& .MuiSlider-thumb': { backgroundColor: immersightBlue75 },
                  }}
                />
              </Grid>
            </>
          )}

          {/* --- ADDED: TEXTURE ATTRIBUTES SECTION --- */}
          {model2D.texture &&
            model2D.texture.textureOrigin === ETextureOrigin.PRODUCT &&
            (() => {
              const prodTexture = model2D.texture as IProductTexture;
              const textureAttrs = getProductTextureAttributes(model2D.texture);

              if (textureAttrs.length > 0) {
                return (
                  <Grid item>
                    <AttributeListFixed attributes={textureAttrs} originRegion={prodTexture.country} />
                  </Grid>
                );
              }
              return null;
            })()}

          <Grid item>
            <Button
              variant="contained"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteModel2D}
              sx={{
                backgroundColor: deleteButton,
                marginTop: '20px',
              }}
            >
              <FormattedMessage id="Model2DDrawer.delete" />
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Drawer>
  );
};

export default Model2DDrawer;
